// UGC video carousel: smooth endless autoscroll (drag to scrub) + custom full screen player.
if (!customElements.get('ora-ugc-videos')) {
  customElements.define(
    'ora-ugc-videos',
    class OraUgcVideos extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.ora-ugc__viewport');
        this.track = this.querySelector('.ora-ugc__track');
        this.items = Array.from(this.track.querySelectorAll('.ora-ugc__item'));
        this.player = this.querySelector('.ora-ugc-player');
        this.video = this.player.querySelector('.ora-ugc-player__video');
        this.progress = this.player.querySelector('.ora-ugc-player__progress');
        this.card = this.player.querySelector('.ora-ugc-player__card');
        this.speed = Number(this.dataset.speed || 0);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.offset = 0;
        this.muted = false;

        this.setupTrack();
        this.setupPlayer();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        document.removeEventListener('keydown', this.onKeyDown);
      }

      /* ---------- Carousel ---------- */

      setupTrack() {
        this.items.forEach((item, index) => {
          item.querySelector('.ora-ugc__tile').addEventListener('click', (event) => {
            if (this.dragged) return event.preventDefault();
            this.open(index);
          });
        });

        // A second copy of the tiles makes the loop seamless.
        this.items.forEach((item, index) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('button, a').forEach((el) => el.setAttribute('tabindex', '-1'));
          clone.querySelector('.ora-ugc__tile').addEventListener('click', (event) => {
            if (this.dragged) return event.preventDefault();
            this.open(index);
          });
          this.track.appendChild(clone);
        });


        // Drag / swipe to scrub.
        this.viewport.addEventListener('pointerdown', (event) => {
          this.pointerStart = event.clientX;
          this.offsetStart = this.offset;
          this.dragging = true;
          this.dragged = false;
        });
        window.addEventListener('pointermove', (event) => {
          if (!this.dragging) return;
          const delta = event.clientX - this.pointerStart;
          if (Math.abs(delta) > 6) this.dragged = true;
          this.offset = this.offsetStart - delta;
          this.applyOffset();
        });
        const endDrag = () => {
          if (!this.dragging) return;
          this.dragging = false;
          // Let the click handler see `dragged`, then reset.
          setTimeout(() => (this.dragged = false));
        };
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);

        this.visible = true;
        this.visibilityObserver = new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting));
        this.visibilityObserver.observe(this);

        this.lastTime = performance.now();
        const step = (time) => {
          const elapsed = Math.min(time - this.lastTime, 64);
          this.lastTime = time;
          const paused = this.reducedMotion || this.dragging || !this.visible;
          if (this.speed > 0 && !paused) {
            this.offset += (this.speed * elapsed) / 1000;
            this.applyOffset();
          }
          this.frame = requestAnimationFrame(step);
        };
        this.frame = requestAnimationFrame(step);
        this.applyOffset();
      }

      applyOffset() {
        const loopWidth = this.track.scrollWidth / 2;
        if (loopWidth > 0) {
          this.offset = ((this.offset % loopWidth) + loopWidth) % loopWidth;
        }
        this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
      }

      /* ---------- Player ---------- */

      setupPlayer() {
        this.progress.innerHTML = this.items.map(() => '<span class="ora-ugc-player__segment"><span></span></span>').join('');
        this.segments = Array.from(this.progress.children);

        this.player.addEventListener('click', (event) => {
          const action = event.target.closest('[data-action]')?.dataset.action;
          if (!action) return;
          if (action === 'close') this.close();
          if (action === 'prev') this.show(this.current - 1);
          if (action === 'next') this.show(this.current + 1);
          if (action === 'toggle') this.onTap(event);
          if (action === 'mute') this.setMuted(!this.muted);
          if (action === 'add') this.addToCart();
        });

        this.video.addEventListener('timeupdate', () => this.updateProgress());
        this.video.addEventListener('ended', () => this.show(this.current + 1));
        this.video.addEventListener('play', () => this.player.classList.remove('is-paused'));
        this.video.addEventListener('pause', () => this.player.classList.add('is-paused'));
        this.video.addEventListener('waiting', () => this.player.classList.add('is-loading'));
        this.video.addEventListener('playing', () => this.player.classList.remove('is-loading'));

        this.onKeyDown = (event) => {
          if (this.player.hidden) return;
          if (event.key === 'Escape') this.close();
          if (event.key === 'ArrowLeft') this.show(this.current - 1);
          if (event.key === 'ArrowRight') this.show(this.current + 1);
          if (event.key === ' ' && event.target === document.body) {
            event.preventDefault();
            this.togglePlay();
          }
        };
        document.addEventListener('keydown', this.onKeyDown);

        // Close the player when something is added to the cart, so the cart drawer is visible.
        if (typeof subscribe === 'function') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            if (!this.player.hidden) this.close();
          });
        }
      }

      open(index) {
        this.opener = document.activeElement;
        this.player.hidden = false;
        document.body.classList.add('overflow-hidden');
        this.show(index);
        this.player.querySelector('[data-action="close"].ora-ugc-player__icon').focus({ preventScroll: true });
      }

      close() {
        if (this.player.hidden) return;
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
        this.player.hidden = true;
        document.body.classList.remove('overflow-hidden');
        if (this.opener) this.opener.focus({ preventScroll: true });
      }

      show(index) {
        const count = this.items.length;
        this.current = ((index % count) + count) % count;
        const item = this.items[this.current];
        const data = item.dataset;

        this.segments.forEach((segment, i) => {
          segment.classList.toggle('is-done', i < this.current);
          segment.firstElementChild.style.width = i < this.current ? '100%' : '0%';
        });

        this.video.poster = data.poster || '';
        this.video.src = data.src;
        this.video.muted = this.muted;
        const playing = this.video.play();
        if (playing) {
          playing.catch(() => {
            // Autoplay with sound can be blocked: retry muted and show the unmute button.
            this.setMuted(true);
            this.video.play().catch(() => this.player.classList.add('is-paused'));
          });
        }

        if (data.productUrl) {
          this.card.hidden = false;
          this.card.querySelector('.ora-ugc-player__card-link').href = data.productUrl;
          this.card.querySelector('.ora-ugc-player__card-title').textContent = data.productTitle;
          this.card.querySelector('.ora-ugc-player__card-price').textContent = data.productPrice;
          const image = this.card.querySelector('.ora-ugc-player__card-image');
          image.src = data.productImage || '';
          image.hidden = !data.productImage;
          const add = this.card.querySelector('.ora-ugc-player__add');
          add.hidden = !data.variantId;
          add.dataset.variantId = data.variantId || '';
          add.disabled = false;
          add.textContent = this.dataset.addLabel;
        } else {
          this.card.hidden = true;
        }
      }

      updateProgress() {
        const segment = this.segments[this.current];
        if (!segment || !this.video.duration) return;
        segment.firstElementChild.style.width = `${(this.video.currentTime / this.video.duration) * 100}%`;
      }

      onTap(event) {
        // On phones, tapping the left/right edge goes to the previous/next video, like stories.
        if (window.matchMedia('(max-width: 749px)').matches) {
          const rect = this.player.querySelector('.ora-ugc-player__frame').getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          if (x < 0.25) return this.show(this.current - 1);
          if (x > 0.75) return this.show(this.current + 1);
        }
        this.togglePlay();
      }

      togglePlay() {
        if (this.video.paused) {
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      }

      setMuted(muted) {
        this.muted = muted;
        this.video.muted = muted;
        this.player.classList.toggle('is-muted', muted);
        this.player.querySelector('[data-action="mute"]').setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      }

      addToCart() {
        const button = this.card.querySelector('.ora-ugc-player__add');
        const variantId = button.dataset.variantId;
        if (!variantId) return;
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        button.disabled = true;

        const body = { items: [{ id: Number(variantId), quantity: 1 }] };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body: JSON.stringify(body) })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description);
            if (!cart) {
              window.location = routes.cart_url;
              return;
            }
            this.close();
            cart.renderContents(response);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ora-ugc', cartData: response });
          })
          .catch(() => {
            button.disabled = false;
          });
      }
    }
  );
}
